/**
 * RecipeForm Component
 *
 * Dialog form for creating/editing substrate recipes with ingredients.
 * Part of US-P2-003: Substrate Production & Block Creation
 *
 * Features:
 * - Parts-based ingredient ratios with percentage display
 * - Toggle between Parts and Percentage view modes
 * - Inventory item linking for cost tracking
 * - Compatible species selection
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  ToggleGroup,
  ToggleGroupItem
} from '@/components/ui/toggle-group'
import {
  useRecipeMutations,
  useRecipes,
  getNextExperimentalVersion
} from '@/hooks/useSubstrateResources'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import { useRecipeCost } from '@/hooks/useRecipeCost'
import { useStrains } from '@/hooks/useLC'
import RecipeCostSummary from '@/components/inventory/RecipeCostSummary'
import { AlertTriangle, Plus, FlaskConical, X, Trash2, Droplets } from 'lucide-react'

/**
 * Format a number as GBP currency
 */
function formatCurrency(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '—'
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

export default function RecipeForm({
  isOpen,
  onClose,
  onSave,
  editingRecipe = null
}) {
  const [formData, setFormData] = useState({
    name: '',
    compatible_species: [],
    mixer_capacity_kg: 243,
    blocks_per_batch: 54, // Keep for backend compatibility
    notes: '',
    ingredients: [],
    version_major: 1,
    version_minor: 0,
    parent_recipe_id: null
  })
  const [isExperimental, setIsExperimental] = useState(false)
  const [selectedParentId, setSelectedParentId] = useState(null)
  const [ratioMode, setRatioMode] = useState('parts') // 'parts' | 'percentage'
  const [error, setError] = useState(null)

  const { createRecipe, updateRecipe, loading: saving } = useRecipeMutations()
  const { recipes: allRecipes } = useRecipes({ activeOnly: true })
  const { allItems: inventoryItems, isLoading: itemsLoading } = useInventoryItems({ activeOnly: true })
  const { recipeCost, isLoading: costLoading, calculateCost } = useRecipeCost()
  const { strains } = useStrains()

  // Get strain codes for species selection (from database)
  const speciesOptions = useMemo(() => {
    return strains.map(s => s.code).sort()
  }, [strains])

  // Filter main recipes only (version_minor = 0) for parent selection
  const mainRecipes = useMemo(() => {
    return (allRecipes || []).filter(r => r.version_minor === 0)
  }, [allRecipes])

  // Calculate total parts for percentage calculations
  const totalParts = useMemo(() => {
    return formData.ingredients.reduce(
      (sum, ing) => sum + (parseFloat(ing.parts) || 0),
      0
    )
  }, [formData.ingredients])

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (editingRecipe) {
        setFormData({
          name: editingRecipe.name || '',
          compatible_species: editingRecipe.compatible_species || [],
          mixer_capacity_kg: editingRecipe.mixer_capacity_kg || 243,
          blocks_per_batch: editingRecipe.blocks_per_batch || 54,
          notes: editingRecipe.notes || '',
          ingredients: editingRecipe.ingredients?.map(ing => ({
            id: ing.id,
            material_name: ing.material_name,
            parts: ing.parts ?? ing.quantity_kg, // Fall back to quantity_kg for legacy
            item_id: ing.item_id || null,
            track_inventory: ing.track_inventory !== false,
            notes: ing.notes || ''
          })) || [],
          version_major: editingRecipe.version_major || 1,
          version_minor: editingRecipe.version_minor || 0,
          parent_recipe_id: editingRecipe.parent_recipe_id || null
        })
        setIsExperimental(false)
        setSelectedParentId(null)
      } else {
        setFormData({
          name: '',
          compatible_species: [],
          mixer_capacity_kg: 243,
          blocks_per_batch: 54,
          notes: '',
          ingredients: [],
          version_major: 1,
          version_minor: 0,
          parent_recipe_id: null
        })
        setIsExperimental(false)
        setSelectedParentId(null)
      }
      setError(null)
    }
  }, [isOpen, editingRecipe])

  // Calculate cost when recipe has an ID (editing mode)
  useEffect(() => {
    if (isOpen && editingRecipe?.id) {
      calculateCost(editingRecipe.id)
    }
  }, [isOpen, editingRecipe?.id, calculateCost])

  // Update version and parent when experimental toggle changes or parent is selected
  useEffect(() => {
    if (!isOpen || editingRecipe) return

    if (isExperimental && selectedParentId) {
      const parentRecipe = mainRecipes.find(r => r.id === selectedParentId)
      if (parentRecipe) {
        const nextVersion = getNextExperimentalVersion(parentRecipe, allRecipes || [])
        setFormData(prev => ({
          ...prev,
          version_major: nextVersion.version_major,
          version_minor: nextVersion.version_minor,
          parent_recipe_id: selectedParentId,
          compatible_species: parentRecipe.compatible_species || []
        }))
      }
    } else if (!isExperimental) {
      setFormData(prev => ({
        ...prev,
        version_major: 1,
        version_minor: 0,
        parent_recipe_id: null
      }))
    }
  }, [isExperimental, selectedParentId, mainRecipes, allRecipes, isOpen, editingRecipe])

  // Build cost lookup from recipeCost for ingredient rows
  const ingredientCostMap = useMemo(() => {
    if (!recipeCost?.ingredients) return {}
    const map = {}
    for (const ing of recipeCost.ingredients) {
      if (ing.item_id) {
        map[ing.item_id] = ing
      }
      if (ing.material_name) {
        map[ing.material_name.toLowerCase()] = ing
      }
    }
    return map
  }, [recipeCost])

  // Get cost data for a specific ingredient
  const getCostDataForIngredient = useCallback((ingredient) => {
    if (ingredient.item_id && ingredientCostMap[ingredient.item_id]) {
      return ingredientCostMap[ingredient.item_id]
    }
    if (ingredient.material_name) {
      return ingredientCostMap[ingredient.material_name.toLowerCase()] || null
    }
    return null
  }, [ingredientCostMap])

  const handleExperimentalToggle = (checked) => {
    setIsExperimental(checked)
    if (!checked) {
      setSelectedParentId(null)
    }
  }

  const handleParentSelection = (parentId) => {
    setSelectedParentId(parentId)
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  const handleAddSpecies = (species) => {
    if (!formData.compatible_species.includes(species)) {
      setFormData(prev => ({
        ...prev,
        compatible_species: [...prev.compatible_species, species]
      }))
    }
  }

  const handleRemoveSpecies = (species) => {
    setFormData(prev => ({
      ...prev,
      compatible_species: prev.compatible_species.filter(s => s !== species)
    }))
  }

  const handleAddIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [
        ...prev.ingredients,
        {
          material_name: '',
          parts: '',
          item_id: null,
          track_inventory: true,
          notes: ''
        }
      ]
    }))
  }

  const handleIngredientChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) =>
        i === index ? { ...ing, [field]: value } : ing
      )
    }))
  }

  const handleIngredientItemSelect = (index, itemId) => {
    if (itemId === 'none') {
      handleIngredientChange(index, 'item_id', null)
      handleIngredientChange(index, 'track_inventory', false)
    } else {
      const selectedItem = inventoryItems.find(item => item.id === itemId)
      if (selectedItem) {
        setFormData(prev => ({
          ...prev,
          ingredients: prev.ingredients.map((ing, i) =>
            i === index
              ? {
                  ...ing,
                  item_id: itemId,
                  material_name: selectedItem.name,
                  track_inventory: true
                }
              : ing
          )
        }))
      }
    }
  }

  const handleRemoveIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('Recipe name is required')
      return
    }

    if (!editingRecipe && isExperimental && !selectedParentId) {
      setError('Please select a parent recipe for experimental variants')
      return
    }

    const invalidIngredients = formData.ingredients.filter(
      ing => !ing.material_name.trim() || !ing.parts
    )
    if (invalidIngredients.length > 0) {
      setError('All ingredients must have a name and parts value')
      return
    }

    try {
      const recipeData = {
        name: formData.name.trim(),
        compatible_species: formData.compatible_species,
        mixer_capacity_kg: parseFloat(formData.mixer_capacity_kg) || 243,
        blocks_per_batch: parseInt(formData.blocks_per_batch) || 54,
        version_major: formData.version_major,
        version_minor: formData.version_minor,
        parent_recipe_id: formData.parent_recipe_id,
        notes: formData.notes.trim() || null,
        ingredients: formData.ingredients.map(ing => ({
          material_name: ing.material_name.trim(),
          parts: parseFloat(ing.parts),
          item_id: ing.item_id || null,
          track_inventory: ing.track_inventory !== false,
          notes: ing.notes?.trim() || null
        }))
      }

      let savedRecipe
      if (editingRecipe) {
        savedRecipe = await updateRecipe({ id: editingRecipe.id, ...recipeData })
      } else {
        savedRecipe = await createRecipe(recipeData)
      }

      onSave?.(savedRecipe)
    } catch (err) {
      setError(err.message || 'Failed to save recipe')
    }
  }

  const hasCostData = editingRecipe?.id && recipeCost && !costLoading

  // Check if ingredient is water (no inventory tracking)
  const isWaterIngredient = (ingredient) => {
    const materialName = (ingredient.material_name || '').toLowerCase()
    return materialName.includes('water') || ingredient.track_inventory === false
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            {editingRecipe ? 'Edit Recipe' : 'Create Recipe'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Recipe Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Recipe Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Masters Mix"
            />
          </div>

          {/* Experimental Recipe Toggle - Only show when creating new recipe */}
          {!editingRecipe && (
            <div className="space-y-4 p-4 bg-surface-secondary rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="experimental-toggle" className="text-base">
                    Create as Experimental Recipe
                  </Label>
                  <p className="text-sm text-content-secondary">
                    Experimental recipes are variants of main recipes for testing modifications
                  </p>
                </div>
                <Switch
                  id="experimental-toggle"
                  checked={isExperimental}
                  onCheckedChange={handleExperimentalToggle}
                />
              </div>

              {isExperimental && (
                <div className="space-y-2">
                  <Label htmlFor="parent-recipe">Parent Recipe *</Label>
                  <Select
                    value={selectedParentId || ''}
                    onValueChange={handleParentSelection}
                  >
                    <SelectTrigger id="parent-recipe" className="w-full">
                      <SelectValue placeholder="Select a main recipe to base this on..." />
                    </SelectTrigger>
                    <SelectContent>
                      {mainRecipes.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-content-secondary">
                          No main recipes available
                        </div>
                      ) : (
                        mainRecipes.map(recipe => (
                          <SelectItem key={recipe.id} value={recipe.id}>
                            {recipe.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>

                  {selectedParentId && formData.version_minor > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        v{formData.version_major}.{formData.version_minor}
                      </Badge>
                      <span className="text-content-secondary">
                        Will be created as experimental version
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Compatible Species */}
          <div className="space-y-2">
            <Label>Compatible Species</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.compatible_species.map(species => (
                <Badge
                  key={species}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {species}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-odin-red"
                    onClick={() => handleRemoveSpecies(species)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {speciesOptions
                .filter(s => !formData.compatible_species.includes(s))
                .map(species => (
                  <Badge
                    key={species}
                    variant="outline"
                    className="cursor-pointer hover:bg-surface-secondary"
                    onClick={() => handleAddSpecies(species)}
                  >
                    + {species}
                  </Badge>
                ))}
            </div>
          </div>

          {/* Ingredients Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Label>Ingredients</Label>
                <ToggleGroup
                  type="single"
                  value={ratioMode}
                  onValueChange={(value) => value && setRatioMode(value)}
                  size="sm"
                >
                  <ToggleGroupItem value="parts" className="text-xs px-3">
                    Parts
                  </ToggleGroupItem>
                  <ToggleGroupItem value="percentage" className="text-xs px-3">
                    %
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddIngredient}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Ingredient
              </Button>
            </div>

            {formData.ingredients.length === 0 ? (
              <div className="text-center py-6 text-content-secondary border-2 border-dashed rounded-md">
                No ingredients added. Click "Add Ingredient" to start.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Material</TableHead>
                    <TableHead className="w-[100px]">
                      {ratioMode === 'parts' ? 'Parts' : '%'}
                    </TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead className="text-right">Est. Cost</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.ingredients.map((ingredient, index) => {
                    const costData = getCostDataForIngredient(ingredient)
                    const isWater = isWaterIngredient(ingredient)
                    const partsValue = parseFloat(ingredient.parts) || 0
                    const percentage = totalParts > 0
                      ? ((partsValue / totalParts) * 100).toFixed(1)
                      : '0.0'

                    return (
                      <TableRow key={index}>
                        {/* Material Select */}
                        <TableCell>
                          <Select
                            value={ingredient.item_id || 'none'}
                            onValueChange={(value) => handleIngredientItemSelect(index, value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue>
                                {ingredient.item_id ? (
                                  ingredient.material_name
                                ) : isWater ? (
                                  <span className="flex items-center gap-1">
                                    <Droplets className="h-3 w-3" />
                                    {ingredient.material_name || 'Water'}
                                  </span>
                                ) : (
                                  ingredient.material_name || 'Select...'
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                <span className="flex items-center gap-1 text-content-secondary">
                                  <Droplets className="h-3 w-3" />
                                  No inventory (e.g., water)
                                </span>
                              </SelectItem>
                              {inventoryItems.map(item => (
                                <SelectItem key={item.id} value={item.id}>
                                  {item.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        {/* Parts or Percentage */}
                        <TableCell>
                          {ratioMode === 'parts' ? (
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              placeholder="0"
                              value={ingredient.parts}
                              onChange={(e) => handleIngredientChange(index, 'parts', e.target.value)}
                              className="w-full"
                            />
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-sm">{percentage}</span>
                              <span className="text-content-tertiary text-xs">%</span>
                            </div>
                          )}
                        </TableCell>

                        {/* Unit Cost */}
                        <TableCell className="text-right">
                          {isWater ? (
                            <span className="text-xs text-content-tertiary">—</span>
                          ) : costLoading || itemsLoading ? (
                            <span className="text-xs text-content-tertiary">...</span>
                          ) : costData?.estimated_unit_cost ? (
                            <span className="text-sm">
                              {formatCurrency(costData.estimated_unit_cost)}
                            </span>
                          ) : (
                            <span className="text-xs text-content-tertiary">—</span>
                          )}
                        </TableCell>

                        {/* Est. Cost */}
                        <TableCell className="text-right">
                          {isWater ? (
                            <span className="text-xs text-content-tertiary">—</span>
                          ) : costLoading || itemsLoading ? (
                            <span className="text-xs text-content-tertiary">...</span>
                          ) : costData?.cost_per_batch ? (
                            <span className="text-sm font-medium">
                              {formatCurrency(costData.cost_per_batch)}
                            </span>
                          ) : (
                            <span className="text-xs text-content-tertiary">—</span>
                          )}
                        </TableCell>

                        {/* Delete */}
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveIngredient(index)}
                            className="text-odin-red hover:text-odin-red/80 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}

                  {/* Total row */}
                  <TableRow className="bg-surface-secondary">
                    <TableCell className="font-medium">Total</TableCell>
                    <TableCell>
                      {ratioMode === 'parts' ? (
                        <span className="font-mono">{totalParts.toFixed(1)}</span>
                      ) : (
                        <span className="font-mono">100%</span>
                      )}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right font-medium">
                      {hasCostData && formatCurrency(recipeCost.total_cost_per_batch)}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {/* Hint text */}
            {!editingRecipe && formData.ingredients.length > 0 && (
              <p className="text-xs text-content-tertiary">
                Enter relative proportions. Actual kg calculated at batch creation based on total weight.
              </p>
            )}
          </div>

          {/* Cost Summary - only show when editing */}
          {hasCostData && (
            <RecipeCostSummary
              recipeCost={recipeCost}
              isLoading={costLoading}
            />
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any special instructions or variations..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (editingRecipe ? 'Update Recipe' : 'Create Recipe')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
